import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import {
  clampView,
  followPlayHead,
  isWholeView,
  MIN_VIEW_SEC,
  panView,
  pointerToSec,
  timelineView,
  ZOOM_STEP,
  zoomViewAt,
  type TimelineView,
  type TimelineZoom,
} from './timelineGeometry';

interface UseTimelineViewProps {
  lanesRef: RefObject<HTMLDivElement | null>;
  range: { startSec: number; endSec: number };
  section: Pick<MusicVideoSongMapSection, 'startSec' | 'endSec'> | null;
  positionSec: number;
  isPlaying: boolean;
}

/** True while the user types in a field: the zoom keys stay off. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * The visible part of the timeline: "Whole song", "Section", or a part that the user zoomed to.
 * Zoom: the + and − buttons and keys (around the play head), Ctrl/⌘ with the wheel and a trackpad
 * pinch (around the pointer). Scroll: the horizontal wheel, Shift with the wheel, the scroll bar.
 * While the song plays, a zoomed view turns the page with the play head.
 */
export function useTimelineView({ lanesRef, range, section, positionSec, isPlaying }: UseTimelineViewProps) {
  const [zoom, setZoom] = useState<TimelineZoom>('song');
  const [custom, setCustom] = useState<TimelineView | null>(null);
  const [laneWidth, setLaneWidth] = useState(0);
  const { startSec, endSec } = range;

  const view = useMemo(() => {
    if (zoom === 'custom' && custom !== null) {
      return clampView(custom.from, custom.to - custom.from, { startSec, endSec });
    }
    return timelineView(zoom === 'custom' ? 'song' : zoom, { startSec, endSec }, section);
  }, [custom, endSec, section, startSec, zoom]);
  const live = useRef(view);
  live.current = view;

  const showView = useCallback(
    (next: TimelineView) => {
      if (isWholeView(next, { startSec, endSec })) {
        setZoom('song');
        setCustom(null);
        return;
      }
      setCustom(next);
      setZoom('custom');
    },
    [endSec, startSec]
  );

  const zoomBy = useCallback(
    (factor: number, anchorSec?: number) => {
      const { current } = live;
      const playInside = positionSec >= current.from && positionSec <= current.to;
      const anchor = anchorSec ?? (playInside ? positionSec : (current.from + current.to) / 2);
      showView(zoomViewAt(current, { startSec, endSec }, factor, anchor));
    },
    [endSec, positionSec, showView, startSec]
  );
  const zoomIn = useCallback(() => zoomBy(1 / ZOOM_STEP), [zoomBy]);
  const zoomOut = useCallback(() => zoomBy(ZOOM_STEP), [zoomBy]);
  const scrollTo = useCallback(
    (from: number) => showView(clampView(from, live.current.to - live.current.from, { startSec, endSec })),
    [endSec, showView, startSec]
  );

  // The width of the lanes, for the cut handles (`showsEdge`).
  useEffect(() => {
    const lanes = lanesRef.current;
    if (lanes === null) {
      return undefined;
    }
    setLaneWidth(lanes.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) {
        setLaneWidth(entry.contentRect.width);
      }
    });
    observer.observe(lanes);
    return () => observer.disconnect();
  }, [lanesRef]);

  // The wheel: Ctrl/⌘ (and a pinch, which sends Ctrl) zooms around the pointer; a horizontal
  // wheel or Shift scrolls a zoomed view. A plain vertical wheel keeps scrolling the page.
  useEffect(() => {
    const lanes = lanesRef.current;
    if (lanes === null) {
      return undefined;
    }
    const onWheel = (event: WheelEvent) => {
      const { current } = live;
      const rect = lanes.getBoundingClientRect();
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        // A mouse wheel notch (about 100) zooms one fifth; a pinch sends many small steps.
        const step = Math.abs(event.deltaY) >= 50 ? Math.sign(event.deltaY) * 0.22 : event.deltaY * 0.01;
        const factor = Math.min(2, Math.max(0.5, Math.exp(step)));
        showView(zoomViewAt(current, { startSec, endSec }, factor, pointerToSec(event.clientX, rect, current)));
        return;
      }
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;
      const delta = horizontal !== 0 ? horizontal : event.shiftKey ? event.deltaY : 0;
      if (delta === 0 || isWholeView(current, { startSec, endSec }) || rect.width <= 0) {
        return;
      }
      event.preventDefault();
      showView(panView(current, { startSec, endSec }, (delta / rect.width) * (current.to - current.from)));
    };
    lanes.addEventListener('wheel', onWheel, { passive: false });
    return () => lanes.removeEventListener('wheel', onWheel);
  }, [endSec, lanesRef, showView, startSec]);

  // The keys: + (or =) zooms in, − zooms out, 0 shows the whole song.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) {
        return;
      }
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomIn();
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        zoomOut();
      } else if (event.key === '0') {
        event.preventDefault();
        setZoom('song');
        setCustom(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomIn, zoomOut]);

  // A zoomed view follows the play head while the song plays.
  useEffect(() => {
    if (!isPlaying || zoom !== 'custom') {
      return;
    }
    const next = followPlayHead(live.current, { startSec, endSec }, positionSec);
    if (next !== live.current) {
      setCustom(next);
    }
  }, [endSec, isPlaying, positionSec, startSec, zoom]);

  const setPresetZoom = useCallback((next: TimelineZoom) => {
    setZoom(next);
    setCustom(null);
  }, []);

  const span = view.to - view.from;
  return {
    zoom,
    setZoom: setPresetZoom,
    view,
    pxPerSec: span > 0 ? laneWidth / span : 0,
    zoomIn,
    zoomOut,
    canZoomIn: span > Math.min(MIN_VIEW_SEC, endSec - startSec) + 0.001,
    canZoomOut: !isWholeView(view, { startSec, endSec }),
    scrollTo,
  };
}
